import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { ChartColorPaletteId } from '../types';
import { CHART_COLOR_PALETTES, CHART_PALETTE_IDS, CHART_PALETTE_LABELS } from '../chartColorPalettes';

export interface SettingsPalettePickerProps {
  value: ChartColorPaletteId;
  onChange: (v: ChartColorPaletteId) => void;
  disabled?: boolean;
  label?: string;
}

function PaletteSwatches({ colors, size = 16 }: { colors: string[]; size?: number }) {
  return (
    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
      {colors.slice(0, 8).map((hex, i) => (
        <Box
          key={i}
          sx={{
            width: size,
            height: size,
            borderRadius: '2px',
            bgcolor: hex,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      ))}
    </Stack>
  );
}

const SettingsPalettePicker: React.FC<SettingsPalettePickerProps> = ({
  value,
  onChange,
  disabled,
  label = 'Color palette',
}) => {
  const selectedColors = CHART_COLOR_PALETTES[value];

  return (
    <Stack spacing={1}>
      <FormControl size="small" fullWidth>
        <InputLabel id="palette-picker-label">{label}</InputLabel>
        <Select
          labelId="palette-picker-label"
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value as ChartColorPaletteId)}
          disabled={disabled}
          renderValue={(val) => (
            <Stack direction="row" spacing={1} alignItems="center">
              <PaletteSwatches colors={CHART_COLOR_PALETTES[val]} size={12} />
              <Typography variant="body2">{CHART_PALETTE_LABELS[val]}</Typography>
            </Stack>
          )}
        >
          {CHART_PALETTE_IDS.map((pid) => (
            <MenuItem key={pid} value={pid}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <PaletteSwatches colors={CHART_COLOR_PALETTES[pid]} size={14} />
                <Typography variant="body2" sx={{ fontWeight: pid === value ? 700 : 400 }}>
                  {CHART_PALETTE_LABELS[pid]}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {selectedColors.map((hex) => (
          <Box
            key={hex}
            sx={{
              width: 22,
              height: 22,
              borderRadius: 0.5,
              bgcolor: hex,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default SettingsPalettePicker;
